import { DeleteIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  Flex,
  IconButton,
  IconButtonProps,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Page } from "../../models/Paged";
import ConfirmDialog, { ConfirmDialogHandle } from "../ConfirmDialog";
import ErrorSection from "../ErrorSection";
import NoItemsCard from "../NoItemsCard";
import Pagination from "../Pagination";

type BaseItem = {
  id?: string | number;
};

type Props<T> = {
  items: T[];
  headers: string[];
  rowExtractors: ((item: T) => string)[];
  onEditClick?: (item: T) => void;
  onDeleteConfirm?: (item: T) => void;
  isDeletionPending?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  refetch?: (page: { page: number }) => void;
  pagination?: Page;
  extraActions?: (item: T) => IconButtonProps[];
  canEdit?: boolean;
  canDelete?: boolean;
};

export default function GenericTable<T extends BaseItem>({
  items,
  headers,
  rowExtractors,
  onEditClick,
  onDeleteConfirm,
  isDeletionPending,
  isLoading,
  isError,
  refetch,
  pagination,
  extraActions,
  canEdit = true,
  canDelete = true,
}: Props<T>) {
  const { t } = useTranslation();
  const itemToDelete = useRef<T>();
  const confirmDialogRef = useRef<ConfirmDialogHandle>(null);

  useEffect(() => {
    refetch?.({ page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = useCallback((item: T) => {
    itemToDelete.current = item;
    confirmDialogRef.current?.open();
  }, []);

  const confirmDeletion = useCallback(() => {
    onDeleteConfirm?.(itemToDelete.current!);
    itemToDelete.current = undefined;
    confirmDialogRef.current?.close();
  }, [onDeleteConfirm]);

  if (isLoading) return <Spinner color="primary.DEFAULT" />;

  if (isError)
    return (
      <ErrorSection
        retry={() => refetch?.({ page: pagination?.number ?? 0 })}
      />
    );

  if (!items.length) return <NoItemsCard />;

  return (
    <>
      <TableContainer borderColor="gray.200" borderWidth={1} borderRadius={8}>
        <Table variant="simple">
          <Thead>
            <Tr>
              {[...headers, t("tables.actions")].map((header) => (
                <Th key={header} textAlign="center">
                  {header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {items?.map((item) => (
              <Tr key={item.id}>
                {rowExtractors.map((rowExtractor, index) => (
                  <Td key={index}>{rowExtractor(item)}</Td>
                ))}
                <Td>
                  <Flex gap={2} justifyContent="center">
                    {extraActions?.(item).map((action, index) => (
                      <Tooltip key={index} label={action["aria-label"]}>
                        <IconButton {...action} />
                      </Tooltip>
                    ))}
                    {canEdit && (
                      <Tooltip label={t("common.buttons.edit")}>
                        <IconButton
                          onClick={() => onEditClick?.(item)}
                          aria-label="Edit"
                          icon={<SettingsIcon />}
                        />
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip label={t("common.buttons.cancel")}>
                        <IconButton
                          onClick={() => openDialog(item)}
                          bg="red.100"
                          color="red.800"
                          aria-label="Delete"
                          isLoading={isDeletionPending}
                          icon={<DeleteIcon />}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {pagination && (
          <Pagination
            pagination={pagination}
            goToPage={(page) => refetch?.({ page })}
          />
        )}
      </TableContainer>
      <ConfirmDialog
        ref={confirmDialogRef}
        title={t("tables.deletion")}
        message={t("tables.confirm_deletion")}
        onConfirm={confirmDeletion}
        isLoading={isDeletionPending}
      />
    </>
  );
}
